//#region Imports

import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgClass } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Crud } from '../../shared/services/crud/crud.service';
import { UserPayload } from '../../shared/types/payloads/user.payload';
import { MeService } from '../../shared/services/me/me.service';
import { UserCourseService } from '../../shared/services/user-course/user-course.service';
import { TooltipModule } from 'primeng/tooltip';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import type { UserCourse } from '../../shared/models/interfaces/user-course.interface';
import { ProfileMenuEnum } from '../../shared/models/enums/profile-menu.enum';
import { ProfileInformationComponent } from './profile-information/profile-information.component';
import { ProfilePasswordComponent } from './profile-password/profile-password.component';
import { ProfileCertificatesComponent } from './profile-certificates/profile-certificates.component';
import { HasPermission } from '../../shared/directives/has-permission/has-permission.directive';
import { PermissionEnum, ResourceEnum } from '../../../environments/shared-environment/permission';
import type { User } from '../../shared/models/interfaces/user.interface';
import { AvatarComponent } from '../../shared/components/avatar/avatar.component';
import { placeholderAvatarUrl } from '../../shared/utils/variables';

//#endregion

interface UserForm {
  name: FormControl<string>;
  email: FormControl<string>;
  password: FormControl<string>;
}

@Component({
    selector: 'l-profile',
    templateUrl: './profile.component.html',
    imports: [
      NgClass,
      FormsModule,
      IconComponent,
      ReactiveFormsModule,
      TooltipModule,
      AvatarComponent,
      SpinnerComponent,
      ProfileInformationComponent,
      ProfilePasswordComponent,
      ProfileCertificatesComponent,
      HasPermission,
    ]
})
export class ProfileComponent extends Crud<UserForm> implements OnInit, OnDestroy {

  constructor(
    private readonly meService: MeService,
    private readonly userCourseService: UserCourseService,
  ) {
    super();
  }

  public isLoading: boolean = false;

  public userName: string = '';

  public userEmail: string = '';

  public user: User | null = null;

  public userAvatarUrl: string = '';

  public isProfileEditing = false;

  public completedCount: number = 0;

  public inProgressCount: number = 0;

  public pendingCount: number = 0;

  public readonly profileMenuEnum: typeof ProfileMenuEnum = ProfileMenuEnum;

  public activeMenu: ProfileMenuEnum = ProfileMenuEnum.PERSONAL_INFO;

  public pendingAvatarFile: File | null = null;

  public completedUserCourses: UserCourse[] = [];

  public readonly certificatesPermission: [ResourceEnum, PermissionEnum] = [ResourceEnum.CERTIFICATES, PermissionEnum.VIEW];

  public override form = new FormGroup<UserForm>(<UserForm>{
    name: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required]),
  });

  public override mapField = {
    name: 'name',
    email: 'email',
  };

  private objectAvatarUrl: string | null = null;

  public override async ngOnInit(): Promise<void> {
    const user = this.meService.getMe();
    if (!user) return;

    this.onUserChange(user);

    const [completed, inProgress, all] = await Promise.all([
      this.userCourseService.getCompletedCourses(user.id),
      this.userCourseService.getInProgressCourses(user.id),
      this.userCourseService.getUserCourses(user.id),
    ]);

    this.completedCount = completed.length;
    this.inProgressCount = inProgress.length;
    this.pendingCount = all.length - completed.length - inProgress.length;
    this.completedUserCourses = completed;
  }

  public setActiveMenu(menu: ProfileMenuEnum): void {
    this.activeMenu = menu;
  }

  public onProfileEditingChange(isEditing: boolean): void {
    this.isProfileEditing = isEditing;

    if (!isEditing) {
      this.discardPendingAvatar();
    }
  }

  public onUserChange(user: User): void {
    this.user = user;
    this.userName = user.name;
    this.userEmail = user.email;
    this.userAvatarUrl = user.imageUrl ?? placeholderAvatarUrl(user.email);
  }

  public openAvatarPicker(input: HTMLInputElement): void {
    if (!this.isProfileEditing) {
      return;
    }

    input.click();
  }

  public onAvatarSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file?.type.startsWith('image/')) {
      return;
    }

    if (this.objectAvatarUrl) {
      URL.revokeObjectURL(this.objectAvatarUrl);
    }

    this.pendingAvatarFile = file;
    this.objectAvatarUrl = URL.createObjectURL(file);
    this.userAvatarUrl = this.objectAvatarUrl;
    input.value = '';
  }

  public override ngOnDestroy(): void {
    if (this.objectAvatarUrl) {
      URL.revokeObjectURL(this.objectAvatarUrl);
    }
  }

  public override async initialize(): Promise<void> {
    if (!this.isUpdate || !this.id)
      return;

    const user = this.meService.me();

    if (!user)
      return;

    this.form.patchValue({
      name: user.name,
      email: user.email,
    });
  }

  public async submit(): Promise<void> {
    const payload = this.form.getRawValue() as UserPayload;
    console.log(payload);
  }

  private discardPendingAvatar(): void {
    this.pendingAvatarFile = null;

    if (this.objectAvatarUrl) {
      URL.revokeObjectURL(this.objectAvatarUrl);
      this.objectAvatarUrl = null;
    }

    if (this.user) {
      this.userAvatarUrl = this.user.imageUrl ?? placeholderAvatarUrl(this.user.email);
    }
  }

}
